import React from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TextField,
  LinearProgress,
  Button,
  Tooltip,
  IconButton,
} from '@mui/material';

import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatMoney(value) {
  const numberValue = Number(value || 0);

  if (numberValue >= 1000000) {
    return thousandsSeparator((numberValue / 1000000).toFixed(1)) + "M";
  }

  if (numberValue >= 1000) {
    return thousandsSeparator((numberValue / 1000).toFixed(1)) + "K";
  }

  return thousandsSeparator(numberValue.toFixed(0));
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

class PromotionsOrdersByLocation extends React.Component {

  state = {
    order: 'asc',
    orderBy: null,
  };

  handleRequestSort = (property) => {
    const isAscending = this.state.orderBy === property && this.state.order === 'asc';
    this.setState({ order: isAscending ? 'desc' : 'asc', orderBy: property });
  };

  csvValue = (value) => {
    if (value === null || typeof value === 'undefined') return '""';

    const stringValue = String(value);
    const trimmedValue = stringValue.trimStart();
    const numericValue = /^-?\d+(?:\.\d+)?%?$/.test(trimmedValue.trim());
    const safeValue = !numericValue && /^[=+\-@\t\r]/.test(trimmedValue) ? `'${stringValue}` : stringValue;
    const cleanValue = safeValue.replace(/"/g, '""');

    return `"${cleanValue}"`;
  };

  handleExportCsv = () => {
    const rows = this.props.data || [];

    if (!rows.length) {
      return;
    }

    const periodMode = this.props.periodMode || 'last_30d';
    const meta = this.props.meta || {};
    const currency = meta.currency || 'RON';
    const promotionLabel = this.props.selectedPromotion?.label || 'All promotions';

    const totalOrdersWithClaims = rows.reduce(
      (sum, row) => sum + Number(row.orders_with_claims || 0),
      0
    );

    const totalSalesFromClaims = rows.reduce(
      (sum, row) => sum + Number(row.sales_from_claims || 0),
      0
    );

    const totalDiscountFromClaims = rows.reduce(
      (sum, row) => sum + Number(row.discount_from_claims || 0),
      0
    );

    const totalGrossSalesFromClaims = totalSalesFromClaims + totalDiscountFromClaims;
    const weightedDiscountRate = totalGrossSalesFromClaims > 0
      ? (totalDiscountFromClaims / totalGrossSalesFromClaims) * 100
      : 0;

    const averageDiscountPerOrder = totalOrdersWithClaims > 0
      ? totalDiscountFromClaims / totalOrdersWithClaims
      : 0;

    const highCostLocations = rows.filter(
      (row) => Number(row.discount_rate || 0) > 15
    ).length;

    const topLocation = [...rows].sort(
      (a, b) => Number(b.orders_with_claims || 0) - Number(a.orders_with_claims || 0)
    )[0];

    const headers = [
      { key: 'location_name', label: 'Location' },
      { key: 'orders_with_claims', label: 'Orders' },
      { key: 'orders_share', label: 'Share %' },
      { key: 'sales_from_claims', label: `Net order value (${currency})` },
      { key: 'discount_from_claims', label: `Discount (${currency})` },
      { key: 'discount_rate', label: 'Discount rate (% of gross promo sales)' },
      { key: 'average_discount', label: `Avg. discount (${currency})` },
    ];

    const csvRows = [
      ['Promo orders by location'].map((value) => this.csvValue(value)).join(','),
      ['Period mode', periodMode].map((value) => this.csvValue(value)).join(','),
      ['Date from', meta.dateFrom || ''].map((value) => this.csvValue(value)).join(','),
      ['Date to', meta.dateTo || ''].map((value) => this.csvValue(value)).join(','),
      ['Timezone', meta.timezone || ''].map((value) => this.csvValue(value)).join(','),
      ['Currency', currency].map((value) => this.csvValue(value)).join(','),
      ['Promotion filter', promotionLabel].map((value) => this.csvValue(value)).join(','),
      ['Generated at', new Date().toLocaleString()].map((value) => this.csvValue(value)).join(','),
      '',
      ['Summary'].map((value) => this.csvValue(value)).join(','),
      ['Total promo orders', totalOrdersWithClaims].map((value) => this.csvValue(value)).join(','),
      ['Total net order value', totalSalesFromClaims.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Total discount', totalDiscountFromClaims.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Weighted discount rate (% of gross promo sales)', `${weightedDiscountRate.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Average discount per promo order', averageDiscountPerOrder.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Locations with discount rate over 15%', highCostLocations].map((value) => this.csvValue(value)).join(','),
      ['Top location by promo orders', topLocation ? topLocation.location_name || 'Unknown' : ''].map((value) => this.csvValue(value)).join(','),
      '',
      headers.map((header) => this.csvValue(header.label)).join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            if (header.key === 'orders_share' || header.key === 'discount_rate') {
              return this.csvValue(`${Number(row[header.key] || 0).toFixed(1)}%`);
            }

            if (
              header.key === 'sales_from_claims' ||
              header.key === 'discount_from_claims' ||
              header.key === 'average_discount'
            ) {
              return this.csvValue(Number(row[header.key] || 0).toFixed(2));
            }

            return this.csvValue(row[header.key]);
          })
          .join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute(
      'download',
      `promo_orders_by_location_${meta.dateFrom || periodMode}_${meta.dateTo || ''}${meta.promotionIref ? `_promotion_${meta.promotionIref}` : ''}.csv`
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  render() {
    const rows = this.props.data || [];
    const meta = this.props.meta || {};
    const promotionOptions = this.props.promotionOptions || [
      { value: 'all', label: 'All promotions', name: 'All promotions' },
    ];
    const selectedPromotion = this.props.selectedPromotion || promotionOptions[0];
    const promotionFiltered = String(selectedPromotion?.value || 'all') !== 'all';
    const { order, orderBy } = this.state;
    const sortedRows = orderBy
      ? [...rows].sort((a, b) => {
          const aValue = orderBy === 'location_name'
            ? String(a.location_name || '').toLocaleLowerCase()
            : Number(a[orderBy] || 0);
          const bValue = orderBy === 'location_name'
            ? String(b.location_name || '').toLocaleLowerCase()
            : Number(b[orderBy] || 0);
          if (aValue < bValue) return order === 'asc' ? -1 : 1;
          if (aValue > bValue) return order === 'asc' ? 1 : -1;
          return 0;
        })
      : rows;

    const sortLabel = (property, label) => (
      <TableSortLabel
        active={orderBy === property}
        direction={orderBy === property ? order : 'asc'}
        onClick={() => this.handleRequestSort(property)}
      >
        {label}
      </TableSortLabel>
    );
    return (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: { xs: '14px', sm: '16px' },
          boxShadow: 'none',
          border: 'none',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: 1.25, sm: 2 },
            '&:last-child': { pb: { xs: 1.25, sm: 2 } },
          }}
        >
          <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ flexShrink: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: '#111827',
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    Promo orders by location
                  </Typography>
                  <Tooltip
                    title="Locations are where claims were consumed. Cost above 15% is highlighted red."
                    arrow
                    describeChild
                    enterTouchDelay={0}
                    leaveTouchDelay={5000}
                  >
                    <IconButton
                      size="small"
                      aria-label="About promo orders by location"
                      sx={{ p: 0.25, color: '#64748b' }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadRoundedIcon />}
                disabled={!rows.length}
                onClick={this.handleExportCsv}
                sx={{
                  flexShrink: 0,
                  height: 32,
                  minWidth: 64,
                  px: 1,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: { xs: '0.68rem', sm: '0.75rem' },
                  whiteSpace: 'nowrap',
                  color: '#111827',
                  borderColor: 'rgba(15, 23, 42, 0.14)',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: 'rgba(15, 23, 42, 0.24)',
                  },
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                    ml: 0,
                  },
                }}
              >
                CSV
              </Button>
            </Stack>

            <Autocomplete
              size="small"
              disableClearable
              autoHighlight
              loading={Boolean(this.props.promotionLoading)}
              disabled={Boolean(this.props.promotionDisabled)}
              options={promotionOptions}
              value={selectedPromotion}
              onChange={this.props.onPromotionChange}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => (
                String(option.value) === String(value.value)
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Promotion"
                  placeholder="Search promotion"
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': 'Promo orders by location filter',
                  }}
                />
              )}
              sx={{
                width: { xs: '100%', sm: 300 },
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  fontSize: { xs: '16px', sm: '0.8rem' },
                },
                '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                  fontSize: { xs: '16px', sm: '0.8rem' },
                },
              }}
            />

            <TableContainer
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: 'auto',
                borderRadius: '12px',
                border: '1px solid rgba(15, 23, 42, 0.06)',
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: { xs: 720, lg: 680 },
                  '& .MuiTableCell-root': {
                    fontSize: { xs: '0.7rem', sm: '0.78rem', lg: '0.72rem', xl: '0.76rem' },
                    py: { xs: 0.7, sm: 0.9 },
                    px: { xs: 0.75, sm: 1, lg: 0.65, xl: 0.8 },
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiTableCell-root:first-of-type': {
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    bgcolor: '#ffffff',
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 800,
                    color: '#1a237e',
                    bgcolor: '#f8fafc',
                  },
                  '& .MuiTableCell-head:first-of-type': {
                    zIndex: 3,
                    bgcolor: '#f8fafc',
                  },
                  '& .MuiTableRow-hover:hover .MuiTableCell-body:first-of-type': {
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{sortLabel('location_name', 'Location')}</TableCell>
                    <TableCell align="right">{sortLabel('orders_with_claims', 'Orders')}</TableCell>
                    <TableCell align="right">{sortLabel('orders_share', 'Share')}</TableCell>
                    <TableCell align="right">{sortLabel('sales_from_claims', 'Net value')}</TableCell>
                    <TableCell align="right">{sortLabel('discount_from_claims', 'Discount')}</TableCell>
                    <Tooltip title={(meta.definitions && meta.definitions.discount_rate) || 'Red indicates a promotion discount above 15% of gross promotion-linked sales.'} arrow>
                      <TableCell align="right">{sortLabel('discount_rate', 'Gross disc. %')}</TableCell>
                    </Tooltip>
                    <TableCell align="right">{sortLabel('average_discount', 'Avg.')}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedRows.map((row, index) => {
                    const ordersShare = Number(row.orders_share || 0);
                    const discountRate = Number(row.discount_rate || 0);
                    const locationName = row.location_name || 'Unknown';

                    return (
                      <TableRow key={`${row.location_iref || 'loc'}-${index}`} hover>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: '#111827',
                            maxWidth: 180,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <Tooltip title={locationName} arrow>
                            <Box component="span">{locationName}</Box>
                          </Tooltip>
                        </TableCell>

                        <TableCell align="right">
                          {thousandsSeparator(Number(row.orders_with_claims || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right" sx={{ minWidth: { xs: 110, lg: 90 } }}>
                          <Stack spacing={0.45}>
                            <Typography
                              sx={{
                                fontSize: 'inherit',
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                            >
                              {formatPercent(ordersShare)}
                            </Typography>

                            <LinearProgress
                              variant="determinate"
                              value={Math.min(ordersShare, 100)}
                              sx={{
                                height: 5,
                                borderRadius: 999,
                                bgcolor: 'rgba(15, 23, 42, 0.08)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 999,
                                  bgcolor: '#1a237e',
                                },
                              }}
                            />
                          </Stack>
                        </TableCell>

                        <TableCell align="right">
                          {formatMoney(row.sales_from_claims)}
                        </TableCell>

                        <TableCell align="right">
                          {formatMoney(row.discount_from_claims)}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 800,
                            color: discountRate > 15 ? '#b91c1c' : '#111827',
                          }}
                        >
                          {formatPercent(discountRate)}
                        </TableCell>

                        <TableCell align="right">
                          {formatMoney(row.average_discount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {promotionFiltered
                          ? `No location activity for ${selectedPromotion.name || 'the selected promotion'}.`
                          : 'No promotion location data available.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default PromotionsOrdersByLocation;
